// Shared presentational pieces for the study mockups.
import React, { useCallback, useState, useRef, useEffect } from "react";
import { defFor, formatValue, labelFor, sourceEvidenceForField } from "./mockData";
import { FLAG_FOR_FINAL_REVIEW, NOT_REVIEWED } from "./conditionContract";
import { FORM_SCHEMA } from "./formSchema";
import { useVisibilityTelemetry } from "./telemetry";

const SCHEMA_DEFS = {};
for (const item of FORM_SCHEMA) {
  if (item.structure_type === "regular") {
    SCHEMA_DEFS[item.id] = item;
  }
  for (const field of item.fields || []) {
    SCHEMA_DEFS[field.id] = field;
  }
  for (const field of item.repeat?.fields || item.fields || []) {
    if (item.structure_type === "repeat_group") SCHEMA_DEFS[`${item.id}.${field.id}`] = field;
  }
}

function fallbackLabel(id) {
  return String(id || "")
    .replace(/[._-]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function cleanQuestion(text) {
  return String(text || "")
    .replace(/\s*\([^)]*\)\s*$/g, "")
    .replace(/\?$/g, "");
}

function schemaDefFor(id) {
  const repeat = String(id || "").match(/^([A-Za-z0-9_-]+)\[(\d+)]\.([A-Za-z0-9_-]+)$/);
  if (repeat) {
    const [, groupId, rawIndex, childId] = repeat;
    const child = SCHEMA_DEFS[`${groupId}.${childId}`] || SCHEMA_DEFS[childId];
    const displayIndex = Number(rawIndex) || 1;
    return child
      ? { ...child, displayLabel: `Vehicle ${displayIndex}: ${cleanQuestion(child.question_text || child.label)}` }
      : { id, displayLabel: `Vehicle ${displayIndex}: ${fallbackLabel(childId)}` };
  }
  return SCHEMA_DEFS[id];
}

function fieldDefFor(id) {
  return schemaDefFor(id) || defFor(id) || { id, type: "text", displayLabel: labelFor(id) };
}

function fieldLabelFor(id) {
  const def = fieldDefFor(id);
  return def.displayLabel || def.label || cleanQuestion(def.question_text) || fallbackLabel(id);
}

function formatReviewValue(value) {
  if (value === null || value === undefined || value === "" || value === "open" || value === "<open>") {
    return "(not filled)";
  }
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (value.every((item) => item && typeof item === "object")) return `${value.length} row${value.length === 1 ? "" : "s"}`;
    return value.join(", ");
  }
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function hasUsableValue(value) {
  return value !== null
    && value !== undefined
    && String(value).trim() !== ""
    && String(value).trim().toLowerCase() !== "open"
    && String(value).trim().toLowerCase() !== "<open>";
}

export function ChatPanel({ messages, title = "Assistant" }) {
  const [draft, setDraft] = useState("");
  return (
    <div className="study-chat">
      <div className="study-chat-head">{title}</div>
      <div className="study-chat-body">
        {messages.map((m, i) => (
          <Bubble key={i} m={m} />
        ))}
      </div>
      <div className="study-chat-input">
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a reply… (mock)"
        />
        <button onClick={() => setDraft("")}>Send</button>
      </div>
    </div>
  );
}

// A single chat bubble (bot / participant).
export function Bubble({ m, onTelemetry }) {
  const messageId = m?.raw?.message_id || m?.raw?.id || m?.id;
  const proposals = m?.payload?.field_proposals || m?.payload?.field_updates || [];
  const mappedFieldIds = Array.from(new Set(
    (Array.isArray(proposals) ? proposals : [])
      .map((proposal) => proposal?.field_id)
      .filter(Boolean)
  ));
  const visibilityRef = useVisibilityTelemetry({
    onTelemetry,
    eventPrefix: "chat_message",
    geometryEventType: "chat_message_geometry_change",
    context: {
      message_id: messageId,
      client_turn_id: m?.raw?.client_turn_id || null,
      reply_to_turn_id: m?.raw?.reply_to_turn_id || null,
      sender: m?.sender,
      field_ids: mappedFieldIds,
      surface: "chat",
    },
    threshold: 0.5,
  });
  return (
    <div
      className={`bubble ${m.sender === "bot" ? "bot" : "me"}`}
      ref={visibilityRef}
      data-message-id={messageId}
    >
      {m.text}
    </div>
  );
}

// Chat frame with an auto-scrolling body. Children make up the timeline.
// When `locked` is set, the composer is disabled during instant verification.
export function ChatShell({
  title = "Assistant",
  children,
  notice = null,
  footer = null,
  locked = false,
  lockedReason = "",
  onLockedAttempt,
  onSend,
  placeholder,
  disabled = false,
  autoScroll = true,
  scrollToBottomKey = null,
}) {
  const bodyRef = useRef();
  const [draft, setDraft] = useState("");
  const inputDisabled = locked || disabled;
  const canSend = !inputDisabled && draft.trim().length > 0;

  useEffect(() => {
    if (autoScroll && scrollToBottomKey !== null && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [autoScroll, scrollToBottomKey]);

  const send = () => {
    const text = draft.trim();
    if (!text || inputDisabled) return;
    if (onSend) onSend(text);
    setDraft("");
  };

  return (
    <div className="study-chat wide">
      <div className="study-chat-head">{title}</div>
      <div className="study-chat-body" ref={bodyRef}>
        {children}
      </div>
      {notice ? <div className="study-chat-notice">{notice}</div> : null}
      {footer ? (
        <div className="study-chat-footer">{footer}</div>
      ) : (
        <div
          className={`study-chat-composer ${inputDisabled ? "locked" : ""}`}
          onPointerDownCapture={() => {
            if (locked && onLockedAttempt) onLockedAttempt();
          }}
        >
          {locked && lockedReason && (
            <div className="study-chat-lock-note" role="status">{lockedReason}</div>
          )}
          <div className={`study-chat-input ${inputDisabled ? "locked" : ""}`}>
            <textarea
              rows="2"
              aria-label="Your answer"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onPaste={(event) => event.preventDefault()}
              onKeyDown={(e) => {
                if (e.key === "Enter" && !e.shiftKey) {
                  e.preventDefault();
                  send();
                }
              }}
              placeholder={
                locked
                  ? "Resolve the pending change to continue…"
                  : placeholder || "Type a reply…"
              }
              disabled={inputDisabled}
            />
            <button onClick={send} disabled={!canSend}>Send</button>
          </div>
        </div>
      )}
    </div>
  );
}

// Sidebar role-play brief so participants don't have to memorize the scenario.
export function ScenarioPanel({ scenario, onTelemetry, activeFieldIds = [] }) {
  const panelNodeRef = useRef(null);
  const lastScrollTopRef = useRef(0);
  const normalizedActiveFieldIds = Array.isArray(activeFieldIds)
    ? activeFieldIds.filter(Boolean)
    : [];
  const activeFieldKey = normalizedActiveFieldIds.join("|");
  const activeFields = new Set(normalizedActiveFieldIds);
  const panelRef = useVisibilityTelemetry({
    onTelemetry,
    eventPrefix: "source_panel",
    context: { surface: "source_panel" },
    threshold: 0.25,
  });
  const setPanelRef = useCallback((node) => {
    panelNodeRef.current = node;
    panelRef.current = node;
  }, [panelRef]);
  const emitMedia = (eventType, event) => {
    if (!onTelemetry) return;
    const media = event.currentTarget;
    onTelemetry({
      event_type: eventType,
      surface: "source_panel",
      media_time_s: Number.isFinite(media.currentTime) ? Math.round(media.currentTime * 10) / 10 : null,
      media_duration_s: Number.isFinite(media.duration) ? Math.round(media.duration * 10) / 10 : null,
    });
  };
  const handleScroll = (event) => {
    const node = event.currentTarget;
    const previousScrollTop = lastScrollTopRef.current;
    lastScrollTopRef.current = node.scrollTop;
    if (onTelemetry) {
      onTelemetry({
        event_type: "source_panel_scroll",
        surface: "source_panel",
        scroll_top_px: Math.round(node.scrollTop || 0),
        scroll_left_px: Math.round(node.scrollLeft || 0),
        scroll_height_px: Math.round(node.scrollHeight || 0),
        scroll_width_px: Math.round(node.scrollWidth || 0),
        viewport_height_px: Math.round(node.clientHeight || 0),
        viewport_width_px: Math.round(node.clientWidth || 0),
        scroll_direction: node.scrollTop > previousScrollTop
          ? "forward"
          : node.scrollTop < previousScrollTop ? "backward" : "stationary",
      });
    }
  };

  useEffect(() => {
    if (!activeFieldKey || !onTelemetry) return;
    onTelemetry({
      event_type: "source_highlight_shown",
      surface: "source_panel",
      field_ids: activeFieldKey.split("|").filter(Boolean),
    });
  }, [activeFieldKey, onTelemetry]);

  return (
    <aside
      className="scenario-panel"
      ref={setPanelRef}
      onScroll={handleScroll}
      aria-label="Scenario"
      tabIndex="-1"
    >
      <header className="scenario-head">
        <h2>{scenario.title || "Scenario"}</h2>
        {scenario.intro && <p>{scenario.intro}</p>}
        {activeFields.size > 0 && (
          <p className="scenario-highlight-note">Highlighted text relates to the current step.</p>
        )}
      </header>
      {scenario.audio?.src && (
        <div className="scenario-media scenario-audio">
          <div className="sf-label">{scenario.audio.label || "Audio"}</div>
          <audio
            controls
            preload="metadata"
            src={scenario.audio.src}
            onPlay={(event) => emitMedia("source_audio_play", event)}
            onPause={(event) => emitMedia("source_audio_pause", event)}
            onEnded={(event) => emitMedia("source_audio_end", event)}
            onSeeked={(event) => emitMedia("source_audio_seek", event)}
          >
            Your browser does not support the audio element.
          </audio>
        </div>
      )}
      {scenario.image?.src && (
        <figure className="scenario-media scenario-figure">
          <img src={scenario.image.src} alt={scenario.image.alt || "Study scenario visualization"} />
        </figure>
      )}
      {scenario.facts.map((f, i) => {
        const segments = Array.isArray(f.segments) && f.segments.length
          ? f.segments
          : [{ value: f.value, fieldIds: [] }];
        const hasActiveSource = segments.some((segment) =>
          (segment.fieldIds || segment.field_ids || [])
            .some((fieldId) => activeFields.has(fieldId))
        );
        return (
          <div className={`scenario-fact ${hasActiveSource ? "has-active-source" : ""}`} key={i}>
            <div className="sf-label">{f.label}</div>
            <div className="sf-value">
              {segments.map((segment, segmentIndex) => {
                const segmentFieldIds = segment.fieldIds || segment.field_ids || [];
                const isActive = segmentFieldIds.some((fieldId) =>
                  activeFields.has(fieldId)
                );
                const sourceFragmentId = segment.sourceFragmentId
                  || segment.source_fragment_id
                  || segment.id
                  || `source.fact_${i}.segment_${segmentIndex}`;
                return (
                  <SourceSegment
                    key={sourceFragmentId}
                    sourceFragmentId={sourceFragmentId}
                    fieldIds={segmentFieldIds}
                    value={segment.value}
                    active={isActive}
                    onTelemetry={onTelemetry}
                    containerRef={panelNodeRef}
                  />
                );
              })}
            </div>
          </div>
        );
      })}
    </aside>
  );
}

function SourceSegment({
  sourceFragmentId,
  fieldIds,
  value,
  active,
  onTelemetry,
  containerRef,
}) {
  const visibilityRef = useVisibilityTelemetry({
    onTelemetry,
    eventPrefix: "source_fragment",
    geometryEventType: "source_fragment_geometry_change",
    context: {
      source_fragment_id: sourceFragmentId,
      mapped_field_ids: fieldIds,
      surface: "source_panel",
    },
    threshold: 0.5,
    containerRef,
  });
  return (
    <span
      className={`source-segment ${active ? "is-active" : ""}`}
      ref={visibilityRef}
      data-source-fragment-id={sourceFragmentId}
      aria-current={active ? "step" : undefined}
    >
      {value}
    </span>
  );
}

// An AI field commit rendered inline in the chat flow (the "pill" card).
export function ChatEdit({ edit, status, editable = false, onChange, children }) {
  return (
    <div className={`chat-edit ${status || ""}`}>
      <div className="chat-edit-head">
        <span className="ce-field">{fieldLabelFor(edit.field_id)}</span>
        <span className="pill ai">AI</span>
        {status && <span className={`pill ${status}`}>{status}</span>}
      </div>
      {editable ? (
        <EditableField id={edit.field_id} value={edit.value} onChange={onChange} />
      ) : (
        <div className="ce-value">{formatValue(edit.value)}</div>
      )}
      {children && <div className="ce-actions">{children}</div>}
    </div>
  );
}

// Read-only or editable value cell driven by field type.
export function EditableField({ id, value, onChange, readOnly = false }) {
  const def = fieldDefFor(id);
  if (readOnly) return <span className="ef-readonly">{formatValue(value)}</span>;

  if (def.type === "single_choice") {
    return (
      <select className="ef" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {(def.options || []).map((o) => (
          <option key={o} value={o}>{o}</option>
        ))}
      </select>
    );
  }
  if (def.type === "bool") {
    return (
      <select className="ef" value={String(value)} onChange={(e) => onChange(e.target.value === "true")}>
        <option value="true">Yes</option>
        <option value="false">No</option>
      </select>
    );
  }
  if (def.type === "multiple_choice") {
    const textValue = Array.isArray(value) ? value.join(", ") : value || "";
    return (
      <input
        className="ef"
        type="text"
        value={textValue}
        onChange={(e) =>
          onChange(e.target.value.split(",").map((v) => v.trim()).filter(Boolean))
        }
      />
    );
  }
  if (def.type === "multiline_text") {
    return <textarea className="ef ef-area" value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  }
  const inputType = def.type === "number" ? "number" : def.type === "date" ? "date" : def.type === "time" ? "time" : "text";
  return <input className="ef" type={inputType} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
}

export function ConditionBadge({ code, label }) {
  return <div className="cond-badge">{code} · {label}</div>;
}

export function ReviewContinueAction({
  continueDisabled = false,
  continuing = false,
  validationMessage = "",
  requiredValidationCopy = "",
  onContinue,
  onBlockedAttempt,
}) {
  return (
    <div
      className="review-submit"
      onPointerDownCapture={() => {
        if (continueDisabled && onBlockedAttempt) onBlockedAttempt();
      }}
    >
      <button
        className="btn primary"
        type="button"
        disabled={continueDisabled || continuing}
        onClick={onContinue}
      >
        {continuing ? "Opening Final Review…" : "Continue to final review"}
      </button>
      {continueDisabled && (
        <span className="review-submit-note" role="status">
          {requiredValidationCopy}
        </span>
      )}
      {validationMessage && !continueDisabled && (
        <span className="review-submit-note error" role="alert">{validationMessage}</span>
      )}
    </div>
  );
}

export function ReviewRequirementCue({ responseRequirement, progressionCue }) {
  return (
    <div
      className="review-requirement-cue"
      data-response-requirement={responseRequirement}
      role="note"
    >
      <strong>{progressionCue?.label}</strong>
      <span>{progressionCue?.text}</span>
    </div>
  );
}

export function ChangeReviewPanel({
  schedule,
  responseRequirement,
  verificationCard,
  sourcePacket,
  edits,
  currentRecord = {},
  currentRecordVersions = {},
  corrections = {},
  reviewStatuses = {},
  mismatchActions = {},
  drafts = {},
  submitted = false,
  onDraftChange,
  onStatusChange,
  onMismatchAction,
  onCorrect,
  onTelemetry,
  showContinue = false,
  continueDisabled = false,
  continuing = false,
  validationMessage = "",
  onContinue,
  onBlockedAttempt,
  savingStatusIds = {},
}) {
  const [correcting, setCorrecting] = useState({});
  const correctionByProposal = {};
  for (const correction of Object.values(corrections || {})) {
    if (correction?.proposal_id) {
      correctionByProposal[correction.proposal_id] = correction;
    }
  }
  const rows = (edits || []).map((edit) => {
    const proposalId = edit.proposal_id || edit.event_id || edit.id;
    const correction = correctionByProposal[proposalId];
    const aiProposedValue = edit.ai_proposed_value !== undefined
      ? edit.ai_proposed_value
      : edit.value;
    const hasCurrentRecordValue = Object.prototype.hasOwnProperty.call(
      currentRecord || {},
      edit.field_id
    );
    const controllerValue = hasCurrentRecordValue
      ? currentRecord[edit.field_id]
      : aiProposedValue;
    const savedValue = correction?.value !== undefined
      ? correction.value
      : controllerValue;
    const controllerEdited = JSON.stringify(controllerValue) !== JSON.stringify(
      aiProposedValue
    );
    const proposalRecordVersion = edit.current_record_version || edit.record_version;
    const controllerRecordVersion = currentRecordVersions[edit.field_id]
      || (controllerEdited ? proposalRecordVersion + 1 : proposalRecordVersion);
    const drafted = Object.prototype.hasOwnProperty.call(drafts, edit.id);
    const value = drafted ? drafts[edit.id] : savedValue;
    return {
      ...edit,
      proposal_id: proposalId,
      ai_proposed_value: aiProposedValue,
      original_value: aiProposedValue,
      saved_value: savedValue,
      value,
      edited: correction !== undefined || controllerEdited,
      current_record_version: correction?.current_record_version
        || controllerRecordVersion,
      status: reviewStatuses[proposalId] || NOT_REVIEWED,
      mismatchAction: mismatchActions[proposalId] || null,
      isCorrecting: Boolean(correcting[edit.id]),
      sourceEvidence: sourceEvidenceForField(
        edit.field_id,
        edit.source_fragment_ids,
        sourcePacket
      ),
    };
  });
  const surface = `${String(schedule || "unknown").toLowerCase()}_review`;

  if (!rows.length) return null;

  return (
    <div
      className="change-review queue"
      data-schedule={schedule}
      data-response-requirement={responseRequirement}
      data-card-version={verificationCard.version}
    >
      <div className="change-review-head">
        <div>
          <p className="review-title">Verification Cards</p>
          <p className="review-sub">Compare every original AI proposal with the relevant scenario details.</p>
        </div>
      </div>

      <div className="review-scroll">
        {rows.map((row, index) => (
          <VerificationReviewRow
            key={row.id}
            row={row}
            position={index + 1}
            total={rows.length}
            verificationCard={verificationCard}
            onDraftChange={onDraftChange}
            onStatusChange={onStatusChange}
            onMismatchAction={onMismatchAction}
            onCorrect={async (row, value) => {
              const saved = await onCorrect(row, value);
              if (!saved) return;
              setCorrecting((current) => {
                const next = { ...current };
                delete next[row.id];
                return next;
              });
            }}
            onTelemetry={onTelemetry}
            saving={Boolean(savingStatusIds[row.id])}
            surface={surface}
            onStartCorrection={() => {
              if (onTelemetry) {
                onTelemetry({
                  event_type: "field_edit_started",
                  surface,
                  field_id: row.field_id,
                  proposal_id: row.proposal_id,
                  proposal_version: row.proposal_version,
                });
              }
              setCorrecting((current) => ({
                ...current,
                [row.id]: true,
              }));
            }}
            onCancelCorrection={() => {
              onDraftChange(row.id, row.saved_value);
              setCorrecting((current) => {
                const next = { ...current };
                delete next[row.id];
                return next;
              });
            }}
          />
        ))}
      </div>

      {showContinue && !submitted && (
        <ReviewContinueAction
          continueDisabled={continueDisabled}
          continuing={continuing}
          validationMessage={validationMessage}
          requiredValidationCopy={verificationCard.required_validation_copy}
          onContinue={onContinue}
          onBlockedAttempt={onBlockedAttempt}
        />
      )}

    </div>
  );
}

function VerificationReviewRow({
  row,
  position,
  total,
  verificationCard,
  onDraftChange,
  onStatusChange,
  onMismatchAction,
  onCorrect,
  onTelemetry,
  onStartCorrection,
  onCancelCorrection,
  surface,
  saving = false,
}) {
  const [mismatchDialogOpen, setMismatchDialogOpen] = useState(false);
  const visibilityRef = useVisibilityTelemetry({
    onTelemetry,
    eventPrefix: "verification_card",
    context: {
      field_id: row.field_id,
      proposal_id: row.proposal_id,
      proposal_version: row.proposal_version,
      source_evidence_id: row.source_evidence_id,
      source_evidence_version: row.source_evidence_version,
      card_order: position,
      surface,
    },
  });
  return (
    <div
      className="review-focus"
      ref={visibilityRef}
      data-field-id={row.field_id}
      aria-busy={saving || undefined}
    >
      <div className="review-focus-meta">
        <span>Current field</span>
        <span>{position} of {total}</span>
      </div>
      <div className="review-focus-label">{fieldLabelFor(row.field_id)}</div>
      <div className="review-status-line">
        <span>Status</span>
        <strong>
          {row.status === NOT_REVIEWED
            ? "Not reviewed"
            : verificationCard.status_options.find((option) => option.value === row.status)?.label
              || row.status.replaceAll("_", " ").toLowerCase()}
        </strong>
        {saving && <span className="review-save-state" role="status">Saving…</span>}
      </div>
      <div className="review-value-block">
        <span className="review-value-label">AI-proposed value</span>
        <div className="review-proposed-value">{formatReviewValue(row.ai_proposed_value)}</div>
      </div>
      {row.edited && (
        <div className="review-value-block current">
          <span className="review-value-label">Current record value</span>
          <div className="review-proposed-value">{formatReviewValue(row.saved_value)}</div>
        </div>
      )}
      <div className="review-evidence" aria-label="Relevant scenario details">
        <span className="review-value-label">Relevant scenario details</span>
        {row.sourceEvidence.map((evidence) => (
          <blockquote key={evidence.sourceFragmentId} data-source-fragment-id={evidence.sourceFragmentId}>
            {evidence.value}
          </blockquote>
        ))}
      </div>
      <p className="review-question">{verificationCard.prompt}</p>
      {row.status === "DOES_NOT_MATCH_SOURCE" && row.edited && (
        <div className="review-outcome-note edited" role="status">
          {verificationCard.mismatch_edited_copy.map((line) => <span key={line}>{line}</span>)}
        </div>
      )}
      {row.status === "DOES_NOT_MATCH_SOURCE" && !row.edited && row.mismatchAction === FLAG_FOR_FINAL_REVIEW && (
        <div className="review-outcome-note flagged" role="status">
          <span>{verificationCard.mismatch_flagged_copy}</span>
        </div>
      )}
      {row.status === "CANNOT_DETERMINE" && (
        <div className="review-outcome-note unclear" role="status">
          <span>{verificationCard.unclear_copy}</span>
        </div>
      )}
      {row.isCorrecting ? (
        <EditableField
          id={row.field_id}
          value={row.value}
          onChange={(value) => onDraftChange(row.id, value)}
          readOnly={saving}
        />
      ) : null}
      <div className="review-actions">
        {row.isCorrecting ? (
          <>
            <button
              className="btn ok sm"
              type="button"
              disabled={saving || !hasUsableValue(row.value) || row.value === row.saved_value}
              onClick={() => onCorrect(row, row.value)}
            >
              {saving ? "Saving…" : "Save edit"}
            </button>
            <button
              className="btn ghost sm"
              type="button"
              disabled={saving}
              onClick={() => {
                onCancelCorrection();
                if (row.status === "DOES_NOT_MATCH_SOURCE" && !row.mismatchAction && !row.edited) {
                  setMismatchDialogOpen(true);
                }
              }}
            >
              Cancel
            </button>
          </>
        ) : (
          <>
            {verificationCard.status_options.map((option) => (
              <button
                className={`btn verify-choice sm ${row.status === option.value ? "selected" : ""}`}
                type="button"
                disabled={saving}
                aria-pressed={row.status === option.value}
                key={option.value}
                onClick={async () => {
                  const selected = row.status === option.value
                    ? true
                    : await onStatusChange(row, option.value);
                  if (
                    selected
                    && option.value === "DOES_NOT_MATCH_SOURCE"
                    && !row.mismatchAction
                    && !row.edited
                  ) {
                    setMismatchDialogOpen(true);
                  }
                }}
              >
                {option.label}
              </button>
            ))}
            {row.status === "DOES_NOT_MATCH_SOURCE" && (row.mismatchAction || row.edited) && (
              <button className="btn ghost sm" type="button" disabled={saving} onClick={onStartCorrection}>
                {verificationCard.edit_action_label}
              </button>
            )}
            {row.status === "CANNOT_DETERMINE" && (
              <button
                className="btn ghost sm"
                type="button"
                disabled={saving}
                onClick={() => {
                  const panel = document.querySelector(".scenario-panel");
                  if (panel) {
                    panel.scrollTo({ top: 0, behavior: "smooth" });
                    panel.focus({ preventScroll: true });
                  }
                  if (onTelemetry) {
                    onTelemetry({
                      event_type: "full_source_requested",
                      surface,
                      field_id: row.field_id,
                      proposal_id: row.proposal_id,
                    });
                  }
                }}
              >
                {verificationCard.view_full_scenario_label}
              </button>
            )}
          </>
        )}
      </div>
      {mismatchDialogOpen && (
        <div className="review-dialog-backdrop">
          <div
            className="review-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby={`mismatch-dialog-${row.id}`}
          >
            <h3 id={`mismatch-dialog-${row.id}`}>{verificationCard.mismatch_dialog_prompt}</h3>
            <div className="review-dialog-actions">
              <button
                className="btn primary"
                type="button"
                disabled={saving}
                onClick={() => {
                  setMismatchDialogOpen(false);
                  onStartCorrection();
                }}
              >
                {verificationCard.edit_action_label}
              </button>
              <button
                className="btn ghost"
                type="button"
                disabled={saving}
                onClick={async () => {
                  const saved = await onMismatchAction(row, FLAG_FOR_FINAL_REVIEW);
                  if (saved) setMismatchDialogOpen(false);
                }}
              >
                {verificationCard.flag_action_label}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
